import React, { useState } from 'react';
import { useEnvironmentMode } from '../../services/productionModeService';
import { toast } from 'react-hot-toast';

interface PurgeMockDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const PurgeMockDataModal: React.FC<PurgeMockDataModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { isProduction, purgeMockData, restoreDemoData } = useEnvironmentMode();
  const [confirmed, setConfirmed] = useState(false);
  const [processing, setProcessing] = useState(false);

  if (!isOpen) return null;

  const handlePurge = () => {
    setProcessing(true);
    setTimeout(() => {
      purgeMockData();
      setProcessing(false);
      toast.success('All mock data & test credentials purged! Platform is now in Live Production Mode.');
      if (onSuccess) onSuccess();
      onClose();
    }, 400);
  };

  const handleRestore = () => {
    setProcessing(true);
    setTimeout(() => {
      restoreDemoData();
      setProcessing(false);
      toast.success('Sandbox demo data & test credentials restored for testing.');
      if (onSuccess) onSuccess();
      onClose();
    }, 400);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md">
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 max-w-lg w-full shadow-2xl relative">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <div className="w-12 h-12 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400 flex items-center justify-center shrink-0">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-black text-white">
              {isProduction ? 'Environment Mode Manager' : 'Purge All Mock Data & Go Live'}
            </h3>
            <p className="text-xs text-slate-400">
              {isProduction
                ? 'Your platform is currently running in Live Production Mode.'
                : 'Prepare your workspace for authentic customers and real devices.'}
            </p>
          </div>
        </div>

        {/* Status Pill */}
        <div className="mb-5 p-3 rounded-2xl bg-slate-950 border border-slate-800 flex items-center justify-between text-xs">
          <span className="text-slate-400 font-semibold">Current Platform State:</span>
          <span
            className={`px-3 py-1 rounded-full font-extrabold uppercase text-[10px] tracking-wider flex items-center gap-1.5 ${
              isProduction
                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                isProduction ? 'bg-emerald-400 animate-pulse' : 'bg-amber-400'
              }`}
            ></span>
            {isProduction ? 'Live Production (Zero Mock Data)' : 'Sandbox Demo Mode'}
          </span>
        </div>

        {/* Detailed Explanation */}
        {!isProduction ? (
          <div className="space-y-3 mb-6 text-xs text-slate-300 bg-slate-950/60 p-4 rounded-2xl border border-white/[0.06]">
            <p className="font-bold text-white mb-2">When you purge mock data:</p>
            <div className="flex items-start gap-2.5">
              <span className="text-emerald-400 font-bold shrink-0">✓</span>
              <span><strong>Quick Test Credentials</strong> will be completely removed from the login screen.</span>
            </div>
            <div className="flex items-start gap-2.5">
              <span className="text-emerald-400 font-bold shrink-0">✓</span>
              <span><strong>Demo hardware assets</strong> (e.g. Perkins, Fujikura, Schneider) will be purged.</span>
            </div>
            <div className="flex items-start gap-2.5">
              <span className="text-emerald-400 font-bold shrink-0">✓</span>
              <span><strong>Mock technician seeds</strong> will be cleared so only genuine registered personnel appear.</span>
            </div>
            <div className="flex items-start gap-2.5">
              <span className="text-emerald-400 font-bold shrink-0">✓</span>
              <span>All local cache stores will be cleanly flushed.</span>
            </div>

            <label className="flex items-center gap-2.5 pt-3 mt-3 border-t border-slate-800 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="w-4 h-4 rounded text-orange-500 focus:ring-orange-500 border-slate-700 bg-slate-900"
              />
              <span className="text-slate-200 font-semibold text-xs">
                I am ready to onboard real users & clear all test data
              </span>
            </label>
          </div>
        ) : (
          <div className="space-y-3 mb-6 text-xs text-slate-300 bg-slate-950/60 p-4 rounded-2xl border border-white/[0.06]">
            <p className="text-emerald-400 font-bold">Platform is operating in clean production mode.</p>
            <p>
              Only authentic user registrations, real physical QR outlet tags, and genuine certified technician repair ledgers will be displayed.
            </p>
            <p className="text-slate-400 pt-2 border-t border-slate-800">
              Need to demonstrate the system to investors or partners? You can temporarily restore sandbox demo data anytime.
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-2xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-xs transition"
          >
            Cancel
          </button>

          {!isProduction ? (
            <button
              type="button"
              onClick={handlePurge}
              disabled={!confirmed || processing}
              className="px-6 py-2.5 rounded-2xl bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 text-white font-extrabold text-xs shadow-lg shadow-orange-500/20 hover:opacity-95 transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {processing ? 'Purging...' : 'Purge All Mock Data & Go Live'}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleRestore}
              disabled={processing}
              className="px-6 py-2.5 rounded-2xl bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/40 font-extrabold text-xs transition flex items-center justify-center gap-2"
            >
              {processing ? 'Restoring...' : 'Restore Sandbox Demo Mode'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
